import {
  Thermometer,
  Snowflake,
  Wind,
  Package,
  AlertTriangle,
  Bell,
  BellOff,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type {
  StorageLocationWithTemp,
  StorageLocationType,
  TemperatureStatus,
} from '../../types/storage';

interface StorageLocationCardProps {
  readonly location: StorageLocationWithTemp;
  readonly onClick?: () => void;
  readonly isSelected?: boolean;
}

const typeIcons: Record<StorageLocationType, typeof Snowflake> = {
  freezer: Snowflake,
  cooler: Wind,
  'dry-storage': Package,
  'bar-storage': Wind,
  pantry: Package,
};

const typeLabels: Record<StorageLocationType, string> = {
  freezer: 'Freezer',
  cooler: 'Cooler',
  'dry-storage': 'Dry Storage',
  'bar-storage': 'Bar Storage',
  pantry: 'Pantry',
};

const statusStyles: Record<TemperatureStatus, string> = {
  normal: 'border-green-200 bg-green-50',
  warning: 'border-amber-200 bg-amber-50',
  critical: 'border-red-200 bg-red-50 animate-pulse',
};

const statusBadgeStyles: Record<TemperatureStatus, string> = {
  normal: 'bg-green-100 text-green-800',
  warning: 'bg-amber-100 text-amber-800',
  critical: 'bg-red-100 text-red-800',
};

const statusLabels: Record<TemperatureStatus, string> = {
  normal: 'Normal',
  warning: 'Warning',
  critical: 'Critical',
};

export function StorageLocationCard({
  location,
  onClick,
  isSelected = false,
}: StorageLocationCardProps) {
  const TypeIcon = typeIcons[location.type];
  const hasAlerts = location.alertConfig.enabled;
  const temp = location.currentTemperature;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full rounded-lg border p-4 text-left transition-all hover:shadow-md',
        statusStyles[location.temperatureStatus],
        isSelected && 'ring-2 ring-primary'
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-white p-2 shadow-sm">
            <TypeIcon className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{location.name}</h3>
            <p className="text-sm text-gray-500">{typeLabels[location.type]}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasAlerts ? (
            <Bell className="h-4 w-4 text-blue-500" />
          ) : (
            <BellOff className="h-4 w-4 text-gray-400" />
          )}
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium',
              statusBadgeStyles[location.temperatureStatus]
            )}
          >
            {location.temperatureStatus !== 'normal' && (
              <AlertTriangle className="h-3 w-3" />
            )}
            {statusLabels[location.temperatureStatus]}
          </span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Thermometer
            className={cn(
              'h-8 w-8',
              location.temperatureStatus === 'normal' && 'text-green-600',
              location.temperatureStatus === 'warning' && 'text-amber-600',
              location.temperatureStatus === 'critical' && 'text-red-600'
            )}
          />
          {temp ? (
            <span className="text-3xl font-bold text-gray-900">
              {temp.value.toFixed(1)}°{temp.unit}
            </span>
          ) : (
            <span className="text-xl text-gray-400">No sensor</span>
          )}
        </div>

        <div className="text-right text-sm">
          <p className="text-gray-500">Items stored</p>
          <p className="font-semibold text-gray-900">{location.itemCount}</p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <span>
          Range: {location.thresholds.minWarning}° to {location.thresholds.maxWarning}°
          {location.thresholds.unit}
        </span>
        {temp && (
          <span>
            Updated {new Date(temp.timestamp).toLocaleTimeString()}
          </span>
        )}
      </div>
    </button>
  );
}
